/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import partnerApiKeyService from "../../services/partner-api-key.service";
import EditPartnerSoftwareModal from "../EditPartnerSoftwareModal";
import { Pagination } from "../Pagination";
import { ROUTES } from "../../constants/routes.constants";
import "./index.scss";

const PartnerSoftwareDataGrid = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
  const [editModalShow, setEditModalShow] = useState(false);
  const [isCreate, setIsCreate] = useState(false);

  const loadList = useCallback(
    async (clientName, pageNum, isActive) => {
      const result = await partnerApiKeyService.List({
        page: pageNum,
        limit,
        clientName: clientName || undefined,
        isActive: isActive === "" ? undefined : Number(isActive),
      });
      if (result.success) {
        setRecords(result.data?.data || []);
        setTotal(result.data?.total || 0);
      }
    },
    [limit]
  );

  const debouncedSearch = debounce((keyword) => {
    setSearchText(keyword);
    setPage(1);
  }, 300);

  useEffect(() => {
    loadList(searchText, page, activeFilter);
  }, [page, activeFilter, searchText, loadList]);

  const getById = async (id) => {
    const result = await partnerApiKeyService.GetById(id);
    if (result.success) {
      setRecord(result.data);
    }
    return result;
  };

  const handleDeactivate = async (row) => {
    const confirm = await Swal.fire({
      title: "Deactivate API key?",
      text: `"${row.clientName}" will no longer be able to call the Partner API.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Deactivate",
    });
    if (!confirm.isConfirmed) return;

    const result = await partnerApiKeyService.Deactivate(row.id);
    if (result.success) {
      loadList(searchText, page, activeFilter);
    }
  };

  return (
    <div className="partner-software-datagrid">
      <div className="card h-100">
        <div className="card-header d-flex justify-content-between align-items-center bg-white flex-wrap gap-2">
          <button
            className="btn btn-default"
            type="button"
            onClick={() => {
              setRecord(null);
              setIsCreate(true);
              setEditModalShow(true);
            }}
          >
            <i className="fa fa-plus-circle me-1"></i>Add platform
          </button>
          <div className="select-menu">
            <select
              className="form-select"
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
          <div className="search-box">
            <i className="fa fa-search"></i>
            <form onSubmit={(event) => event.preventDefault()}>
              <input
                type="text"
                className="form-control rounded-pill"
                placeholder="Search by name…"
                spellCheck="false"
                onChange={(event) => debouncedSearch(event.target.value)}
              />
            </form>
          </div>
        </div>

        <div className="card-body datagrid p-0">
          <table className="table mb-0">
            <thead>
              <tr>
                <th className="text-center">#</th>
                <th>Platform name</th>
                <th className="text-center">Key prefix</th>
                <th className="text-center">Rate limit</th>
                <th className="text-center">Status</th>
                <th className="text-center">Last used</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No partner platforms registered yet.
                  </td>
                </tr>
              ) : (
                records.map((row, index) => (
                  <tr
                    key={row.id}
                    className={index === selectedRowIndex ? "selected" : ""}
                  >
                    <td className="text-center">{row.id}</td>
                    <td>{row.clientName}</td>
                    <td className="text-center key-prefix">
                      <code>{row.prefix}</code>
                    </td>
                    <td className="text-center">{row.rateLimit}</td>
                    <td className="text-center">
                      <span
                        className={`badge bg-${row.isActive ? "success" : "secondary"}`}
                      >
                        {row.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="text-center">
                      {row.lastUsedAt
                        ? moment(row.lastUsedAt).format("MM/DD/YY HH:mm")
                        : "—"}
                    </td>
                    <td>
                      <div className="d-flex justify-content-center">
                        <a
                          href="#"
                          className="view"
                          title="View patients, users & prescriptions"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedRowIndex(index);
                            navigate(
                              ROUTES.PARTNER_PLATFORM_DETAIL_ROUTE.replace(
                                ":id",
                                String(row.id)
                              )
                            );
                          }}
                        >
                          <i className="fa fa-eye"></i>
                        </a>
                        <a
                          href="#"
                          className="edit"
                          title="Edit"
                          onClick={async (e) => {
                            e.preventDefault();
                            setSelectedRowIndex(index);
                            await getById(row.id);
                            setIsCreate(false);
                            setEditModalShow(true);
                          }}
                        >
                          <i className="fa fa-edit"></i>
                        </a>
                        {row.isActive && (
                          <a
                            href="#"
                            className="delete"
                            title="Deactivate"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDeactivate(row);
                            }}
                          >
                            <i className="fa fa-ban"></i>
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          total={total}
          limit={limit}
          setPage={setPage}
          maxPage={20}
        />
      </div>

      <EditPartnerSoftwareModal
        show={editModalShow}
        isCreate={isCreate}
        record={record}
        onHide={async (refresh) => {
          setEditModalShow(false);
          if (refresh) {
            await loadList(searchText, page, activeFilter);
          }
        }}
      />

    </div>
  );
};

export default PartnerSoftwareDataGrid;
